import type { Content } from "@prismicio/client";
import { asText } from "@prismicio/helpers";
import classNames from "classnames";
import { useMemo, type CSSProperties } from "react";
import { useCountdown, resolveLabels, buildParts } from "../useCountdown";

type Props = {
  slice: Content.CountdownTimerSliceFullWidth;
};

const HEX_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;
const MAX_BORDER_RADIUS = 48;
const MIN_TITLE_SIZE = 12;
const MAX_TITLE_SIZE = 48;
const TITLE_FONTS: Record<string, string> = {
  Lato: "'Lato-Local', Lato, sans-serif",
  Poppins: "Poppins, sans-serif",
};

// Empty or invalid editor values fall back to the house-style defaults in custom.css.
const color = (value: string | null | undefined) =>
  value && HEX_COLOR.test(value.trim()) ? value.trim() : undefined;

const radius = (value: number | null | undefined) =>
  typeof value === "number" && value >= 0
    ? `${Math.min(value, MAX_BORDER_RADIUS)}px`
    : undefined;

const fontSize = (value: number | null | undefined) =>
  typeof value === "number" && value > 0
    ? `${Math.min(Math.max(value, MIN_TITLE_SIZE), MAX_TITLE_SIZE)}px`
    : undefined;

const FullWidthTimer = ({ slice }: Props) => {
  const title = asText(slice.primary.title);
  const timeLeft = useCountdown(slice.primary.target_datetime);

  const labels = useMemo(() => resolveLabels(slice.primary), [slice.primary]);

  const parts = buildParts(timeLeft, labels);
  const borderColor = color(slice.primary.border_color);

  const style = useMemo(() => {
    const vars: Record<string, string | undefined> = {
      "--ct-full-bg": color(slice.primary.background_color),
      "--ct-full-border": borderColor,
      "--ct-full-block-bg": color(slice.primary.block_color),
      "--ct-full-title": color(slice.primary.title_color),
      "--ct-full-title-font": slice.primary.title_font
        ? TITLE_FONTS[slice.primary.title_font]
        : undefined,
      "--ct-full-title-size": fontSize(slice.primary.title_font_size),
      "--ct-full-number": color(slice.primary.number_color),
      "--ct-full-label": color(slice.primary.label_color),
      "--ct-full-radius": radius(slice.primary.border_radius),
    };
    return Object.fromEntries(
      Object.entries(vars).filter(([, value]) => value !== undefined)
    ) as CSSProperties;
  }, [slice.primary, borderColor]);

  return (
    <div className="container">
      <div className="row">
        <div className="col-12">
          <div
            className={classNames("countdown-timer-full__box", {
              "countdown-timer-full__box--bordered": borderColor,
            })}
            style={style}
          >
            {title && <h2 className="countdown-timer-full__title">{title}</h2>}

            <div className="countdown-timer-full__units">
              {parts.map((part) => (
                <div key={part.key} className="countdown-timer-full__block">
                  <span
                    className="countdown-timer-full__value"
                    suppressHydrationWarning
                  >
                    {part.value}
                  </span>
                  {slice.primary.show_labels !== false && (
                    <span className="countdown-timer-full__label">
                      {part.label}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FullWidthTimer;
